import _ from 'lodash';
import { buildUrl, denormalizeUrl } from '../../libs/utils';

import {
	PublicClientApplication,
	InteractionRequiredAuthError
} from '@azure/msal-browser';

export const LAST_ACTIVE_ACCOUNT = 'wz:last-active-account';

export default class AzureAD {
	account: any;
	options: any;
	manager: any;

	constructor(options = {}) {
		this.account = null;

		this.options = _.merge(
			{
				client: null,
				authority: null,

				notificationElementClassName: 'app',

				logger: null,

				azure: {}
			},
			options
		);

		this.options.azure = _.merge(
			{
				auth: {
					clientId: this.options.client,
					authority: this.options.authority,

					redirectUri: denormalizeUrl(this.root).toLowerCase(),
					postLogoutRedirectUri: denormalizeUrl(
						this.root
					).toLowerCase()
				},
				cache: {
					cacheLocation: 'localStorage' // This configures where your cache will be stored
				}
			},
			this.options.azure
		);
	}

	get isLoggedIn() {
		return !!this.account;
	}

	get loginParameters() {
		const request: any = {
			scopes: [
				'openid',
				'profile',
				buildUrl(this.host, 'user_impersonation')
			]
		};

		const lastActive = localStorage.getItem(LAST_ACTIVE_ACCOUNT);

		if (lastActive) {
			request.loginHint = lastActive;
		}

		return request;
	}

	get tokenParameters() {
		return {
			...this.loginParameters,
			account: this.account
		};
	}

	get host() {
		return [window.location.protocol, window.location.host].join('//');
	}

	get root() {
		return [
			window.location.protocol,
			[
				window.location.host,
				window.location.pathname.substr(
					1,
					window.location.pathname.lastIndexOf('/')
				)
			]
				.filter(Boolean)
				.join('/')
		].join('//');
	}

	async init() {
		this.manager = new PublicClientApplication(this.options.azure);

		await this.manager.initialize();

		return this.manager
			.handleRedirectPromise()
			.then((tokenResponse: any) => {
				this.authenticate(tokenResponse);

				if (!this.isLoggedIn) {
					return this.login();
				}
			})
			.catch((e: any) => this.notify(e));
	}

	authenticate(tokenResponse: any) {
		if (tokenResponse !== null) {
			this.setAccount(tokenResponse.account);
		} else {
			this.selectAccount();
		}
	}

	login() {
		const parameters = this.loginParameters;

		if (parameters.loginHint) {
			return this.manager
				?.ssoSilent(parameters)
				.then(this.authenticate.bind(this))
				.catch(this.loginGeneric.bind(this));
		}

		return this.loginGeneric();
	}

	loginGeneric() {
		const parameters = this.loginParameters;

		return this.options.popUp
			? this.manager?.loginPopup(parameters)
			: this.manager?.loginRedirect(parameters);
	}

	logout() {
		this.persistLastAccount(false);
		return this.manager?.logoutRedirect();
	}

	setAccount(account: any) {
		this.account = account;

		this.persistLastAccount();
	}

	persistLastAccount(store = true) {
		if (store) {
			if ('username' in this.account) {
				localStorage.setItem(
					LAST_ACTIVE_ACCOUNT,
					String(this.account.username)
				);
			}
		} else {
			localStorage.removeItem(LAST_ACTIVE_ACCOUNT);
		}
	}

	//Get tenant id from authority url
	getTenantId() {
		return this.options.authority
			?.replace(/^\/\/|^.*?:(\/\/)?/, '') //regex to remove protocols from url, since authority is a url
			.split('/')[1];
	}

	selectAccount() {
		//We might have cases where user are authenticated against multiple tenants
		//We need to determine which tenant is being used in order to determine the correct account
		const tenantId = this.getTenantId();
		const currentAccounts = this.manager?.getAllAccounts({
			tenantId
		});

		if (currentAccounts && currentAccounts.length === 1) {
			// no support for multiple accounts yet
			this.setAccount(currentAccounts[0]);
		}
	}

	getAuthorizationHeader() {
		return this.getToken().then((response: any) =>
			this.buildAuthorizationHeader(response)
		);
	}

	buildAuthorizationHeader(token: any) {
		return {
			Authorization: [token?.tokenType, token?.accessToken]
				.filter(Boolean)
				.join(' ')
		};
	}

	getToken() {
		return this.manager
			.acquireTokenSilent(this.tokenParameters)
			.catch((e: any) => {
				if (e instanceof InteractionRequiredAuthError) {
					// fallback to interaction when silent call fails
					return this.manager.acquireTokenRedirect(
						this.tokenParameters
					);
				} else {
					this.notify(e);
				}
			});
	}

	getAccessToken() {
		return this.getToken().then((token: any) => token?.accessToken);
	}

	notify(e: any) {
		const elm = document.getElementsByClassName(
			this.options.notificationElementClassName
		);

		if (elm.length) {
			elm[0].outerHTML = `<div class="ui-critical-error">${e.message}</div>`;
		}
	}
}
